import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import type { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import type { ResultsAvailability } from '../../types/types';

/**
 * Text and toggle defaults, mirroring the builder component's manifest
 * (`extensions/services-list/settings.ts`) so both surfaces read the same out
 * of the box. There is no Settings panel yet — these supply defaults only.
 */
export type ISettingsParams = {
  listTitle: SettingsParamType.Text;
  dailyBadgeLabel: SettingsParamType.Text;
  hourlyBadgeLabel: SettingsParamType.Text;
  bookButtonLabel: SettingsParamType.Text;
  loadMoreButtonLabel: SettingsParamType.Text;
  showHourlyRentals: SettingsParamType.Boolean;
  showDailyRentals: SettingsParamType.Boolean;
  dailySearchResultsAvailability: SettingsParamType.Text;
  showImage: SettingsParamType.Boolean;
  showBadge: SettingsParamType.Boolean;
  showTitle: SettingsParamType.Boolean;
  showPrice: SettingsParamType.Boolean;
  showUnitPrice: SettingsParamType.Boolean;
  showMinDuration: SettingsParamType.Boolean;
  showMaxDuration: SettingsParamType.Boolean;
  showTagline: SettingsParamType.Boolean;
  showBookButton: SettingsParamType.Boolean;
  showSearchLocation: SettingsParamType.Boolean;
  showSearchTime: SettingsParamType.Boolean;
  showFiltersButton: SettingsParamType.Boolean;
  showFilterTags: SettingsParamType.Boolean;
  showListTitle: SettingsParamType.Boolean;
};

const visibleByDefault = {
  type: SettingsParamType.Boolean as const,
  getDefaultValue: () => true,
};

const settingsParams = createSettingsParams<ISettingsParams>({
  listTitle: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('manifest.servicesList.data.title.defaultValue'),
  },
  dailyBadgeLabel: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('manifest.servicesList.data.dailyBadgeLabel.defaultValue'),
  },
  hourlyBadgeLabel: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('manifest.servicesList.data.hourlyBadgeLabel.defaultValue'),
  },
  bookButtonLabel: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('manifest.servicesList.data.bookButtonLabel.defaultValue'),
  },
  loadMoreButtonLabel: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('manifest.servicesList.data.loadMoreButtonLabel.defaultValue'),
  },
  showHourlyRentals: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showDailyRentals: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  dailySearchResultsAvailability: {
    type: SettingsParamType.Text,
    getDefaultValue: () => 'someAvailability',
  },
  showImage: visibleByDefault,
  showBadge: visibleByDefault,
  showTitle: visibleByDefault,
  showPrice: visibleByDefault,
  showUnitPrice: visibleByDefault,
  showMinDuration: visibleByDefault,
  showMaxDuration: visibleByDefault,
  showTagline: visibleByDefault,
  showBookButton: visibleByDefault,
  showSearchLocation: visibleByDefault,
  showSearchTime: visibleByDefault,
  showFiltersButton: visibleByDefault,
  showFilterTags: visibleByDefault,
  showListTitle: visibleByDefault,
});

export default settingsParams;

/* Compared, not asserted: site config can hold anything. */
export function getResultsAvailability(
  settings: ControllerFlowAPI['settings'],
): ResultsAvailability {
  return settings.get(settingsParams.dailySearchResultsAvailability) ===
    'fullyAvailable'
    ? 'fullyAvailable'
    : 'someAvailability';
}
